import axios from 'axios';

export const REQUEST_OUTCOMES = {
  success: 'success',
  failure: 'failure',
  cancelled: 'cancelled',
};

const csrfToken = () => document.getElementsByName('csrf-token')[0].content;

const isCancelled = (error, signal) => (
  signal?.aborted
  || error?.code === 'ERR_CANCELED'
  || error?.name === 'CanceledError'
  || axios.isCancel?.(error)
);

const serializedError = (error) => {
  if (!error || (typeof error !== 'object' && typeof error !== 'function')) {
    return {};
  }
  return JSON.parse(JSON.stringify(error, Object.getOwnPropertyNames(error)));
};

// A 422 carries its detail under `errors`, not `error`: ActiveRecord validation
// failures send `full_messages` strings, while service-level rejections (e.g.
// `Nextgen::DocumentMetadataSubmission::InvalidSubmission`) send
// `{ field, code, message }` objects whose message is the actionable copy. Both
// shapes have to become a plain string here — every consumer reads
// `response.data.error` — or the user sees a generic retry prompt (or worse,
// "[object Object]") instead of the reason the request was rejected.
const errorListMessage = (errors) => {
  if (!Array.isArray(errors)) return undefined;

  const messages = errors.map((entry) => {
    if (typeof entry === 'string') return entry.trim();
    if (entry && typeof entry === 'object') return String(entry.message ?? '').trim();
    return '';
  }).filter(Boolean);

  return messages.length ? messages.join(' ') : undefined;
};

export const normalizeRequestError = (error, {
  fallbackStatus = 520,
  repairIncompleteResponse = true,
} = {}) => {
  const originalResponse = error?.response;
  const status = originalResponse?.status;
  const suppliedMessage = originalResponse?.data?.error;
  // `data.error` is the shape every consumer reads, so a list-only body is
  // folded into it here rather than left for each caller to re-derive. An
  // existing `data.error` always wins, so no current behavior shifts.
  const listMessage = suppliedMessage ? undefined : errorListMessage(originalResponse?.data?.errors);
  const message = suppliedMessage || listMessage;
  let response = listMessage
    ? { ...originalResponse, data: { ...originalResponse.data, error: listMessage } }
    : originalResponse;

  if (!response || (repairIncompleteResponse && (!status || !message))) {
    response = {
      status: fallbackStatus === null ? status : (status || fallbackStatus),
      data: {
        error: message || 'An unexpected error occurred, please try again.',
      },
    };
  }

  return {
    // `crud` returns this object to callers as `{ ...value, error: true }`, and
    // several of them (e.g. `DynamicEhrDrawer` on a chart-upload rejection) read
    // its top-level `message` rather than `response.data.error`. Leaving axios'
    // own "Request failed with status code 422" there would hide the reason we
    // just derived. Only the derived message is promoted: a body that already
    // carried `data.error` keeps whatever `message` it had, so no current
    // behavior shifts.
    ...serializedError(error),
    ...(listMessage ? { message: listMessage } : {}),
    response,
  };
};

const waitForRetry = (delayMs, signal) => new Promise((resolve) => {
  if (signal?.aborted) {
    resolve(false);
    return;
  }

  let timeout;
  const onAbort = () => {
    clearTimeout(timeout);
    resolve(false);
  };
  timeout = setTimeout(() => {
    signal?.removeEventListener('abort', onAbort);
    resolve(true);
  }, delayMs);
  signal?.addEventListener('abort', onAbort, { once: true });
});

export const requestTransport = async (options, {
  maxRetries = 5,
  normalizeError,
  onFailure,
  onRetry,
  onRetryFailed,
  onUnauthorized,
  retries = 0,
  retryDelayMs = 2000,
} = {}) => {
  try {
    const response = await axios({
      ...options,
      headers: {
        ...options.headers,
        'X-CSRF-TOKEN': csrfToken(),
      },
    });
    return {
      outcome: REQUEST_OUTCOMES.success,
      value: response,
    };
  } catch (error) {
    if (isCancelled(error, options.signal)) {
      return { outcome: REQUEST_OUTCOMES.cancelled };
    }

    if (error?.response?.status === 503 && retries <= maxRetries) {
      const shouldRetry = await waitForRetry(retryDelayMs, options.signal);
      if (!shouldRetry) {
        return { outcome: REQUEST_OUTCOMES.cancelled };
      }
      onRetry?.(retries + 1);
      return requestTransport(options, {
        maxRetries,
        normalizeError,
        onFailure,
        onRetry,
        onRetryFailed,
        onUnauthorized,
        retries: retries + 1,
        retryDelayMs,
      });
    }

    if (retries > maxRetries) onRetryFailed?.();
    if (error?.response?.status === 401) onUnauthorized?.(error);
    onFailure?.(error);

    return {
      outcome: REQUEST_OUTCOMES.failure,
      value: normalizeError
        ? normalizeError(error)
        : normalizeRequestError(error),
    };
  }
};
